import { AnyAction } from 'redux'
import requestStatuses, { RequestStatus } from '@cinch/oxd-utils/request-status/src'
import {
  ORDER_CREATE_COMPLETE,
  ORDER_CREATE_REQUEST,
  ORDER_CREATE_ERROR,
  ORDER_DETAILS_REQUEST,
  ORDER_DETAILS_COMPLETE,
  ORDER_DETAILS_ERROR,
  VEHICLE_DETAILS_REQUEST,
  VEHICLE_DETAILS_COMPLETE,
  VEHICLE_DETAILS_ERROR,
  ORDER_DETAILS_UPDATED,
  ORDER_ADD_ADDRESS_REQUEST,
  ORDER_ADD_ADDRESS_COMPLETE,
  ORDER_ADD_ADDRESS_ERROR,
} from '../types'
import { OrderDetails, enrichOrderDetailsResponse } from '@cinch/oxd-pages/order-confirmation/src/types'

export interface RequestStatusWrapper<T> {
  status: RequestStatus
  error?: T
}

export enum OrderDetailsValidationStatus {
  NOT_VALIDATED = 'NOT_VALIDATED', // Data has not yet been retrieved or validated
  PENDING = 'PENDING', // Validation in progress
  VALID = 'VALID', // Current data is valid
  INVALID = 'INVALID', // Current data is invalid and should not be used
}

interface OrderDetailsMeta {
  validationStatus: OrderDetailsValidationStatus
  loaded: boolean // true if successfully loaded at least once
  requestError: boolean // true if the most recent request failed
}

export interface OrderDetailsState {
  data?: OrderDetails
  meta: OrderDetailsMeta
}

export interface OrderCreateRequestError {
  errorType: string
  message: string
}

export interface OrderState {
  orderId?: string // TODO: get rid of this and keep in page state
  orderAddAddressRequestStatus: RequestStatusWrapper<string>
  orderCreateRequestStatus: RequestStatusWrapper<OrderCreateRequestError>
  orderDetailsRequestStatus: RequestStatusWrapper<string>
  orderDetails?: OrderDetailsState
  vehicleDetailsRequestStatus: RequestStatusWrapper<string>
  vehicleDetails?: Cinch.VehicleData
}

export const orderDetailsMetaInitialState: OrderDetailsMeta = {
  validationStatus: OrderDetailsValidationStatus.NOT_VALIDATED,
  loaded: false,
  requestError: false,
}

export function orderDetailsMetaReducer(
  state: OrderDetailsMeta = orderDetailsMetaInitialState,
  action: AnyAction,
): OrderDetailsMeta {
  switch (action.type) {
    case ORDER_DETAILS_REQUEST:
      return {
        ...state,
        validationStatus: OrderDetailsValidationStatus.PENDING,
        requestError: false,
      }
    case ORDER_CREATE_COMPLETE:
    case ORDER_DETAILS_COMPLETE:
    case ORDER_ADD_ADDRESS_COMPLETE:
      return {
        ...state,
        validationStatus: OrderDetailsValidationStatus.VALID,
        loaded: true,
        requestError: false,
      }
    case ORDER_DETAILS_ERROR:
      return {
        ...state,
        validationStatus: OrderDetailsValidationStatus.INVALID,
        loaded: false,
        requestError: true,
      }
    case ORDER_DETAILS_UPDATED:
      return {
        ...state,
        validationStatus: OrderDetailsValidationStatus.VALID,
        loaded: true,
      }
    default:
      return state
  }
}

export const initialState: OrderState = {
  orderAddAddressRequestStatus: { status: requestStatuses.INITIAL },
  orderCreateRequestStatus: { status: requestStatuses.INITIAL },
  orderDetailsRequestStatus: { status: requestStatuses.INITIAL },
  vehicleDetailsRequestStatus: { status: requestStatuses.INITIAL },
  orderDetails: {
    meta: orderDetailsMetaInitialState,
  },
}

export default function orderReducer(state: OrderState = initialState, action: AnyAction): OrderState {
  switch (action.type) {
    case ORDER_ADD_ADDRESS_REQUEST:
      return {
        ...state,
        orderAddAddressRequestStatus: { status: requestStatuses.PENDING },
      }
    case ORDER_ADD_ADDRESS_COMPLETE:
      return {
        ...state,
        orderAddAddressRequestStatus: { status: requestStatuses.SUCCESS },
        orderDetails: {
          data: enrichOrderDetailsResponse(action.payload),
          meta: orderDetailsMetaReducer(state.orderDetails?.meta, action),
        },
      }
    case ORDER_ADD_ADDRESS_ERROR: {
      return {
        ...state,
        orderAddAddressRequestStatus: { status: requestStatuses.ERROR, error: action.payload?.response?.message },
      }
    }
    case ORDER_CREATE_REQUEST:
      return {
        ...state,
        orderCreateRequestStatus: { status: requestStatuses.PENDING },
      }
    case ORDER_CREATE_COMPLETE:
      return {
        ...state,
        orderCreateRequestStatus: { status: requestStatuses.SUCCESS },
        orderDetails: {
          data: enrichOrderDetailsResponse(action.payload),
          meta: orderDetailsMetaReducer(state.orderDetails?.meta, action),
        },
      }
    case ORDER_CREATE_ERROR:
      return {
        ...state,
        orderCreateRequestStatus: { status: requestStatuses.ERROR, error: action.payload },
      }

    case VEHICLE_DETAILS_REQUEST:
      return {
        ...state,
        vehicleDetailsRequestStatus: { status: requestStatuses.PENDING },
      }
    case VEHICLE_DETAILS_COMPLETE:
      return {
        ...state,
        vehicleDetailsRequestStatus: { status: requestStatuses.SUCCESS },
        vehicleDetails: action.payload,
      }
    case VEHICLE_DETAILS_ERROR:
      return {
        ...state,
        vehicleDetailsRequestStatus: { status: requestStatuses.ERROR, error: action.payload },
      }

    case ORDER_DETAILS_REQUEST:
      return {
        ...state,
        orderDetailsRequestStatus: { status: requestStatuses.PENDING },
        orderDetails: {
          data: state.orderDetails?.data,
          meta: orderDetailsMetaReducer(state.orderDetails?.meta, action),
        },
      }
    case ORDER_DETAILS_COMPLETE:
      return {
        ...state,
        orderDetailsRequestStatus: { status: requestStatuses.SUCCESS },
        orderDetails: {
          data: enrichOrderDetailsResponse(action.payload),
          meta: orderDetailsMetaReducer(state.orderDetails?.meta, action),
        },
      }
    case ORDER_DETAILS_ERROR:
      return {
        ...state,
        orderDetailsRequestStatus: { status: requestStatuses.ERROR, error: action.payload },
        orderDetails: {
          meta: orderDetailsMetaReducer(state.orderDetails?.meta, action),
        },
      }

    // Listen for events that return a read-model to give us the latest view of the order
    case ORDER_DETAILS_UPDATED:
      return {
        ...state,
        orderDetails: {
          data: action.payload,
          meta: orderDetailsMetaReducer(state.orderDetails?.meta, action),
        },
      }

    default:
      return state
  }
}
